import { app, auth } from "./init";
import { artistHubAvailable } from "@/lib/artistRollout";
import {
  DmBroadcastSendBody,
  DmInboxFilter,
  DmModerationDecision,
  DmRefundClaimStatus,
  DmReportTarget,
  dmErrorMessage,
  inboxQuery,
} from "@/lib/dm";
import { ArtistOwner, CreatorApplicationStatus, CreatorApproveBody, CreatorKind, FanPassRequestStatus, creatorErrorMessage } from "@/lib/creator";
import {
  ArtistModerationDecision,
  ArtistModerationStatus,
  ArtistModerationType,
  ArtistReportReason,
  moderationErrorMessage,
} from "@/lib/moderation";
import { FeaturedPage, featuredErrorMessage } from "@/lib/featured";
import {
  ArtistAdminListResult,
  ReviewQueuePage,
  artistAdminErrorMessage,
  reviewQueueLimit,
  toAdminList,
  toReviewPage,
} from "@/lib/artistAdmin";

// Explicit production opt-in; this never grants backend roles or changes App Remote Config.
export const artistHubEnabled = artistHubAvailable(app.options.projectId, import.meta.env.VITE_ARTIST_HUB_ENABLED);

export async function artistPublication(action: "get" | "publishProfile" | "unpublishProfile", artistId: string): Promise<boolean> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("請重新登入後操作");
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action, artistId }),
  });
  if (action === "get" && response.status === 404) return false;
  if (!response.ok) throw new Error(response.status === 403 ? "需要管理員或工作人員權限" : "操作未完成，請重試");
  return action !== "unpublishProfile";
}

/* ---------- B-06: artist self-service profile (contract §3 get / §5 profileUpdate) ---------- */

export type ArtistLinkType = "instagram" | "youtube" | "threads" | "facebook" | "other";
export interface ArtistLink { type: ArtistLinkType; url: string; label: string }
/** Full `get` object; also the profileUpdate response. */
export interface ArtistProfile {
  artistId: string;
  displayName: string;
  bio: string;
  avatarPath: string;
  coverPath: string;
  avatarUrl: string | null;
  coverUrl: string | null;
  followerCount: number;
  postCount: number;
  vlogCount: number;
  following: boolean;
  links: ArtistLink[];
  isSelf: boolean;
  /** C-05: `artistProfiles.handle` (contract v2.7); older backends omit it. */
  handle?: string | null;
}
export interface ArtistAuditEntry {
  id: string;
  action: string;
  actorUid: string;
  role?: string;
  target?: string;
  fields?: string[];
  version?: number;
  at: number | null;
  [key: string]: unknown;
}
export interface ArtistProfilePatch { displayName?: string; legalName?: string; bio?: string; links?: { url: string; label?: string }[] }

const messages: Record<string, string> = {
  FORBIDDEN: "需要管理員或工作人員權限",
  UNAUTHENTICATED: "請重新登入後操作",
  NOT_FOUND: "找不到藝人專頁（尚未發布？）",
  INVALID_ARGUMENT: "請檢查字數與連結格式：名稱 ≤30 字、簡介 ≤150 字、連結最多 3 個且必須以 https:// 開頭",
  MEDIA_NOT_READY: "頭像／封面圖片尚未處理完成",
};

async function artistHubRequest<T>(action: string, artistId: string, body: Record<string, unknown> = {}): Promise<T> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("請重新登入後操作");
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action, artistId }),
    signal: AbortSignal.timeout(20000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new Error(messages[data.error ?? ""] ?? "操作未完成，請重試");
  return data as T;
}

/** Published profile as the App sees it; null when the profile is not published (404). */
export async function artistProfileGet(artistId: string): Promise<ArtistProfile | null> {
  try {
    return await artistHubRequest<ArtistProfile>("get", artistId);
  } catch (error) {
    if (error instanceof Error && error.message === messages.NOT_FOUND) return null;
    throw error;
  }
}
/** Owner/staff only; never merge verification names into the public ArtistProfile cache. */
export const artistProfileIdentityGet = (artistId: string) =>
  artistHubRequest<{ artistId: string; legalName: string }>("profileIdentityGet", artistId);

export const artistProfileUpdate = (artistId: string, patch: ArtistProfilePatch) =>
  artistHubRequest<ArtistProfile>("profileUpdate", artistId, { ...patch });
export const artistProfileAudit = async (artistId: string, limit = 20) =>
  (await artistHubRequest<{ items: ArtistAuditEntry[] }>("profileAudit", artistId, { limit })).items;

/* ---------- B-17: 鐵粉 entitlements (staff grant / revoke / list) ---------- */

/** B-23 (contract §1b): `grace` added for coin fan passes that failed to renew. */
export type EntitlementStatus = "active" | "grace" | "expired" | "revoked";
export type EntitlementSource = "coin" | "staff_grant" | "promo" | "iap";
export interface ArtistEntitlement {
  uid: string;
  artistId: string;
  status: EntitlementStatus;
  source: EntitlementSource | null;
  validFrom: number | null;
  validUntil: number | null;
  note: string;
  email: string | null;
  updatedAt: number | null;
  /* §1b extension; older backends omit these. */
  autoRenew?: boolean | null;
  streakMonths?: number | null;
  graceUntil?: number | null;
}
const entitlementMessages: Record<string, string> = {
  FORBIDDEN: "需要管理員或工作人員權限",
  UNAUTHENTICATED: "請重新登入後操作",
  NOT_FOUND: "找不到此 Email／UID 的帳號，或藝人不存在",
  INVALID_ARGUMENT: "請檢查欄位：到期日必須晚於現在、Email 格式正確、備註 ≤200 字",
  TEMPORARILY_UNAVAILABLE: "暫時無法查詢帳號，請稍後再試",
};
async function entitlementRequest<T>(action: string, artistId: string, body: Record<string, unknown> = {}): Promise<T> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("請重新登入後操作");
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action, artistId }),
    signal: AbortSignal.timeout(20000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; reason?: string };
  if (!response.ok) throw new Error(entitlementMessages[data.error ?? ""] ?? "操作未完成，請重試");
  return data as T;
}
export const artistEntitlementList = (artistId: string, cursor?: string | null, limit = 50, status?: EntitlementStatus | null) =>
  entitlementRequest<{ items: ArtistEntitlement[]; nextCursor: string | null }>("entitlementList", artistId, {
    limit,
    ...(cursor ? { cursor } : {}),
    ...(status ? { status } : {}),
  });
/** Grant by Email (resolved to a uid server side); validUntil is epoch ms and required. */
export const artistEntitlementGrant = (artistId: string, target: { email: string } | { uid: string }, validUntil: number, note = "") =>
  entitlementRequest<{ uid: string; artistId: string; status: "active"; validUntil: number }>("entitlementGrant", artistId, {
    ...target,
    validUntil,
    note,
  });
export const artistEntitlementRevoke = (artistId: string, uid: string, note = "") =>
  entitlementRequest<{ uid: string; artistId: string; status: "revoked" }>("entitlementRevoke", artistId, { uid, note });

/* ---------- B-23: 鐵粉月卡（金幣制）CMS actions (contract §1b 創作者／CMS action) ---------- */

export interface FanPassPromo {
  firstPeriodCoins: number;
  /** epoch ms; promo applies to joins before this instant. */
  until: number;
}
export interface FanPassConfig {
  artistId: string;
  enabled: boolean;
  priceCoins: number;
  benefits: string[];
  promo: FanPassPromo | null;
  updatedBy?: string | null;
  updatedAt?: number | null;
}
export interface FanPassConfigInput {
  enabled: boolean;
  priceCoins: number;
  benefits: string[];
  promo?: FanPassPromo | null;
}
export type PayeeType = "person" | "company";
export interface ArtistPayee {
  artistId: string;
  payeeType: PayeeType;
  name: string;
  taxId: string | null;
  /** 0–1; default 0.7. */
  shareRate: number;
  /** epoch ms (start of day, local). Older docs may hold a "YYYY-MM-DD" string. */
  effectiveFrom: number | string | null;
  updatedAt?: number | null;
}
export interface ArtistPayeeInput {
  payeeType: PayeeType;
  name: string;
  taxId?: string;
  shareRate: number;
  effectiveFrom: number;
}
export interface FanPassReportRow {
  artistId: string;
  displayName: string;
  joins: number;
  renews: number;
  paidCoins: number;
  unitPriceNtd: number;
  grossNtd: number;
  platformFeeNtd: number;
  creatorShareNtd: number;
  platformShareNtd: number;
  estimated?: boolean;
}
export type FanPassReportTotals = Omit<FanPassReportRow, "artistId" | "displayName" | "unitPriceNtd"> & { unitPriceNtd?: number | null };
export interface FanPassReport {
  month: string;
  rows: FanPassReportRow[];
  totals: Partial<FanPassReportTotals> | null;
  estimated?: boolean;
}

/** Contract default when `artistFanPass/{artistId}` is missing (the backend returns the same). */
export const FAN_PASS_DEFAULTS: Omit<FanPassConfig, "artistId"> = {
  enabled: false,
  priceCoins: 500,
  benefits: ["鐵粉限定貼文、Vlog 與限動", "鐵粉徽章", "連續月數等級徽章"],
  promo: null,
};
export const PAYEE_DEFAULT_SHARE_RATE = 0.7;

const fanPassMessages: Record<string, string> = {
  FORBIDDEN: "需要管理員或工作人員權限",
  UNAUTHENTICATED: "請重新登入後操作",
  NOT_FOUND: "找不到此藝人",
  INVALID_ARGUMENT: "請檢查欄位：價格 100–5000 金幣、權益最多 6 條且每條 ≤40 字、分潤比例 0–100%",
  FEATURE_NOT_READY: "後端尚未支援此功能（請先部署 B-20～B-23 artistHub）",
};
async function fanPassRequest<T>(action: string, body: Record<string, unknown>): Promise<T> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("請重新登入後操作");
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action }),
    signal: AbortSignal.timeout(30000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!response.ok) {
    const known = fanPassMessages[data.error ?? ""];
    // Unknown action on a backend without B-23 → 400 INVALID_ARGUMENT / unknown action.
    throw new Error(known ?? (data.error ? `操作未完成（${data.error}）` : "操作未完成，請重試"));
  }
  return data as T;
}
const num = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
/** Accepts the bare object or a `{config}` / `{payee}` envelope; fills contract defaults. */
export function normalizeFanPassConfig(artistId: string, raw: unknown): FanPassConfig & { isDefault: boolean } {
  const obj = (raw && typeof raw === "object" && "config" in raw ? (raw as { config: unknown }).config : raw) as Partial<FanPassConfig> | null;
  if (!obj || typeof obj !== "object") return { artistId, ...FAN_PASS_DEFAULTS, isDefault: true };
  const promo = obj.promo && typeof obj.promo === "object" && typeof obj.promo.firstPeriodCoins === "number" ? obj.promo : null;
  return {
    artistId,
    enabled: obj.enabled === true,
    priceCoins: num(obj.priceCoins, FAN_PASS_DEFAULTS.priceCoins),
    benefits: Array.isArray(obj.benefits) ? obj.benefits.filter((b): b is string => typeof b === "string") : [...FAN_PASS_DEFAULTS.benefits],
    promo,
    updatedBy: obj.updatedBy ?? null,
    updatedAt: obj.updatedAt ?? null,
    isDefault: obj.updatedAt == null && obj.updatedBy == null,
  };
}
export function normalizePayee(artistId: string, raw: unknown): ArtistPayee | null {
  const obj = (raw && typeof raw === "object" && "payee" in raw ? (raw as { payee: unknown }).payee : raw) as Partial<ArtistPayee> | null;
  if (!obj || typeof obj !== "object" || !obj.payeeType) return null;
  return {
    artistId,
    payeeType: obj.payeeType === "company" ? "company" : "person",
    name: obj.name ?? "",
    taxId: obj.taxId ?? null,
    shareRate: num(obj.shareRate, PAYEE_DEFAULT_SHARE_RATE),
    effectiveFrom: obj.effectiveFrom ?? null,
    updatedAt: obj.updatedAt ?? null,
  };
}

export const fanPassConfigGet = async (artistId: string) =>
  normalizeFanPassConfig(artistId, await fanPassRequest<unknown>("fanPassConfigGet", { artistId }));
export const fanPassConfigSet = async (artistId: string, input: FanPassConfigInput) =>
  normalizeFanPassConfig(
    artistId,
    await fanPassRequest<unknown>("fanPassConfigSet", {
      artistId,
      enabled: input.enabled,
      priceCoins: input.priceCoins,
      benefits: input.benefits,
      ...(input.promo ? { promo: input.promo } : { promo: null }),
    }),
  );
/** null = no payee on file (404 or empty response). */
export async function payeeGet(artistId: string): Promise<ArtistPayee | null> {
  try {
    return normalizePayee(artistId, await fanPassRequest<unknown>("payeeGet", { artistId }));
  } catch (error) {
    if (error instanceof Error && error.message === fanPassMessages.NOT_FOUND) return null;
    throw error;
  }
}
export const payeeSet = async (artistId: string, input: ArtistPayeeInput) =>
  normalizePayee(artistId, await fanPassRequest<unknown>("payeeSet", { artistId, ...input }));
export const fanPassReport = (month: string) => fanPassRequest<FanPassReport>("fanPassReport", { month });

/* ---------- B-26: per-artist store subscription products (contract §1c 對應表與開通, staff only; served by artistFanProductsAdmin, not artistHub) ---------- */

export type IosFanProductState = "none" | "created" | "in_review" | "approved" | "rejected" | "removed";
export type AndroidFanProductState = "none" | "active" | "inactive";
export interface FanProductStatus {
  artistId: string;
  provisioned: boolean;
  priceTier: number | null;
  saleEnabled: boolean;
  ios: { productId: string | null; state: IosFanProductState; lastError: string | null; ascState?: string | null; pricesComplete?: boolean };
  android: { productId: string | null; basePlanId: string; state: AndroidFanProductState; lastError: string | null };
  updatedAt: number | null;
  updatedBy: string | null;
  /** Only on fanProductStatus. */
  priceTiers?: number[];
  credentialsMissing?: boolean;
  missing?: string[];
}
export const FAN_PRICE_TIERS_DEFAULT = [90, 150, 300, 490];

const fanProductMessages: Record<string, string> = {
  FORBIDDEN: "需要管理員或工作人員權限",
  UNAUTHENTICATED: "請重新登入後操作",
  NOT_FOUND: "找不到此藝人",
  STORE_CREDENTIALS_MISSING: "商店 API 憑證尚未設定（App Store Connect 金鑰／App ID），請先完成 FAN_PASS_STORE_PROVISIONING 文件的設定",
  PROVISION_IN_PROGRESS: "另一個建立作業正在進行，請稍後按「重新整理狀態」",
  PRODUCT_ID_CONFLICT: "商品代號已被其他藝人使用，請聯絡工程",
  PRODUCT_NOT_PROVISIONED: "尚未建立商店商品",
  FAILED_PRECONDITION: "藝人缺少名稱，請先設定藝人名稱",
};
async function fanProductRequest(action: string, body: Record<string, unknown>): Promise<FanProductStatus> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("請重新登入後操作");
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistFanProductsAdmin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action }),
    // Separate function from artistHub (only it binds the store secrets); a first provision can take ~90 s.
    signal: AbortSignal.timeout(130000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; reason?: string };
  if (!response.ok) {
    if (response.status === 404 && !data.error) throw new Error("後端尚未部署 artistFanProductsAdmin（B-26），請先部署");
    if (data.error === "INVALID_ARGUMENT" && data.reason === "priceTier") throw new Error("價格檔位不在允許清單（revenue_config/fan_pass.priceTiers）");
    const known = fanProductMessages[data.error ?? ""];
    if (known) throw new Error(data.error === "STORE_CREDENTIALS_MISSING" && data.reason ? `${known}（${data.reason}）` : known);
    throw new Error(data.error ? `操作未完成（${data.error}${data.reason ? `：${data.reason}` : ""}）` : "操作未完成，請重試");
  }
  return data as FanProductStatus;
}
export const fanProductStatus = (artistId: string) => fanProductRequest("fanProductStatus", { artistId });
export const fanProductProvision = (artistId: string, priceTier: number) => fanProductRequest("fanProductProvision", { artistId, priceTier });
export const fanProductSetSale = (artistId: string, onSale: boolean) => fanProductRequest("fanProductSetSale", { artistId, onSale });

/* ---------- C-01～C-03: 鐵粉私訊 creator + ops actions (contract §1d v2.3 + v2.4; served by artistHub) ---------- */

export type DmChannelStatus = "active" | "paused" | "suspended" | "closed";
export type DmTeamRole = "artist" | "editor" | "staff";
export type DmSenderKind = "artist" | "team";
export type DmBroadcastType = "text" | "image" | "voice" | "video" | "poll";
export type DmBroadcastStatus = "scheduled" | "processing" | "sent" | "retracted" | "cancelled" | "failed";
export interface DmDelivery { minMonthly: number; minWeeks: number; minVoiceOrVideo: number }
export interface DmCreatorRules {
  repliesPerBroadcast: number;
  idleRefillDays: number;
  charLadder: [number, number][];
  visibleBackfillDays: number;
  nicknameCooldownDays: number;
  nicknameMaxChars: number;
  broadcastTextMax: number;
  scheduleMaxDays: number;
  imagesMax: number;
  pollOptionsMin: number;
  pollOptionsMax: number;
  pollMaxDays: number;
  refundWindowDays: number;
  delivery: DmDelivery;
}
export interface DmMonthStats {
  total: number;
  byArtist: number;
  team: number;
  voiceOrVideo: number;
  artistWeeks: string[];
  weeksCovered: number;
}
export interface DmQuietHours { start: string; end: string }
export interface DmChannelConfig {
  artistId: string;
  enabled: boolean;
  status: DmChannelStatus;
  statusReason: string | null;
  welcome: { text: string; updatedAt: number | null; media?: { assetId: string; kind: "voice"; durationMs: number | null } | null } | null;
  quietHours: DmQuietHours | null;
  pausedUntil: number | null;
  pauseNote: string;
  rules: DmCreatorRules;
  stats: { lastBroadcastAt: number | null; lastArtistBroadcastAt: number | null; month: string; current: DmMonthStats };
  role: DmTeamRole;
  canToggle: boolean;
  canEditRules: boolean;
  updatedAt: number | null;
  updatedBy: string | null;
}
export interface DmChannelConfigPatch {
  enabled?: boolean;
  /** B-32: mediaRef = one dm-voice clip (mediaUploadAudio); text may be "" when a clip is attached. */
  welcome?: { text: string; mediaRef?: { assetId: string } } | null;
  quietHours?: DmQuietHours | null;
  pausedUntil?: number | null;
  pauseNote?: string | null;
  rules?: Record<string, unknown> | null;
}
export interface DmMedia {
  assetId: string;
  kind: "image" | "voice" | "video";
  url: string | null;
  expiresAt: number | null;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
}
export interface DmPoll {
  /** v2.4: optional question. */
  question?: string | null;
  options: { id: string; text: string; count: number }[];
  closesAt: number | null;
  closed: boolean;
  totalVotes: number;
  myVote: string | null;
}
export interface DmCreatorBroadcast {
  broadcastId: string;
  artistId: string;
  type: DmBroadcastType;
  text: string | null;
  media: DmMedia[];
  poll: DmPoll | null;
  senderKind: DmSenderKind;
  status: DmBroadcastStatus;
  retracted: boolean;
  sentAt: number | null;
  retractedAt: number | null;
  scheduledAt: number | null;
  cancelledAt: number | null;
  createdAt: number | null;
  senderUid: string;
  replyCount: number;
  heartCount: number;
  fanout: { status: "pending" | "running" | "done"; threads: number } | null;
  /** status "failed": duration | media_failed | timeout (v2.3 B-32). */
  failReason?: string | null;
  /** status "cancelled" by dmScheduledSender: channel_disabled | channel_suspended | channel_closed | expired. */
  cancelReason?: string | null;
}
export interface DmBroadcastSendResult {
  broadcast: DmCreatorBroadcast;
  replay: boolean;
  fanout: { status: "done" | "partial" | "continuing"; threads: number } | null;
}
export interface DmInboxItem {
  replyId: string;
  uid: string;
  nickname: string;
  threadKey: string;
  broadcastId: string | null;
  text: string;
  createdAt: number | null;
  streakDays: number | null;
  streakMonths: number | null;
  hearted: boolean;
  hidden: boolean;
  hiddenBy: "artist" | "ops" | "filter" | null;
  reported: boolean;
  seen: boolean;
  /** v2.4: the fan's thread is blocked by the artist team (dmBlockFan). */
  fanBlocked?: boolean;
}
export interface DmMessage {
  messageId: string;
  kind: "text" | "story_reply";
  from: "fan" | "artist" | "team";
  text: string;
  replyToBroadcastId: string | null;
  inReplyTo: string | null;
  isWelcome: boolean;
  isPrivateReply: boolean;
  hearted: boolean;
  contentId: string | null;
  createdAt: number | null;
}
export interface DmInsights {
  artistId: string;
  month: string;
  role: DmTeamRole;
  total: number;
  byArtist: number;
  team: number;
  voiceOrVideo: number;
  weeksCovered: number;
  delivery: DmDelivery;
  checks: { monthly: boolean; weeks: boolean; voiceOrVideo: boolean };
  met: boolean;
  lastArtistBroadcastAt: number | null;
  daysSinceLastArtistBroadcast: number | null;
  threads: number;
  replyCount: number;
  heartCount: number;
  replyRate: number | null;
  /** B-33: current active / grace fans with the dm perk, by continuous chain day. */
  subscriptionDays?: { buckets: Record<string, number>; total: number; truncated: boolean } | null;
  asOf: number;
}
export interface DmReport {
  reportId: string;
  source: "fan" | "filter" | "artist" | null;
  artistId: string | null;
  uid: string | null;
  reporterUid: string | null;
  target: DmReportTarget | null;
  targetId: string | null;
  threadKey: string | null;
  broadcastId: string | null;
  snippet: string;
  reason: string;
  status: "open" | "resolved";
  resolution: DmModerationDecision | null;
  createdAt: number | null;
  resolvedAt: number | null;
}
export type DmSuspendMode = "suspend" | "close" | "restore";
export interface DmPage<T> { items: T[]; nextCursor: string | null }

/** Error carrying the backend `{error, reason}` so panels can branch on 403 / DM_DISABLED. */
export class DmError extends Error {
  constructor(message: string, readonly code: string, readonly reason: string | undefined, readonly status: number) {
    super(message);
  }
}
async function dmRequest<T>(action: string, body: Record<string, unknown>): Promise<T> {
  if (!artistHubEnabled) throw new DmError("藝人專區尚未開放此環境", "FEATURE_NOT_READY", undefined, 0);
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new DmError("請重新登入後操作", "UNAUTHENTICATED", undefined, 401);
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action }),
    // Immediate sends fan out to every thread inside the call.
    signal: AbortSignal.timeout(action === "dmBroadcastSend" ? 120000 : 30000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; reason?: string };
  if (!response.ok) throw new DmError(dmErrorMessage(data.error, data.reason, response.status), data.error ?? "", data.reason, response.status);
  return data as T;
}
/** Omits null / undefined / "" so optional contract fields are simply absent. */
const opt = (key: string, value: unknown): Record<string, unknown> =>
  value === undefined || value === null || value === "" ? {} : { [key]: value };

/* creator (artist / editor / staff) */
export const dmChannelConfigGet = (artistId: string) => dmRequest<DmChannelConfig>("dmChannelConfigGet", { artistId });
export const dmChannelConfigSet = (artistId: string, patch: DmChannelConfigPatch) =>
  dmRequest<DmChannelConfig>("dmChannelConfigSet", { ...patch, artistId });
export const dmBroadcastSend = (artistId: string, requestId: string, body: DmBroadcastSendBody) =>
  dmRequest<DmBroadcastSendResult>("dmBroadcastSend", { ...body, artistId, requestId });
export const dmBroadcastRetract = (artistId: string, broadcastId: string) =>
  dmRequest<{ broadcast: DmCreatorBroadcast }>("dmBroadcastRetract", { artistId, broadcastId });
export const dmBroadcastList = (artistId: string, opts: { status?: DmBroadcastStatus | null; cursor?: string | null; limit?: number } = {}) =>
  dmRequest<DmPage<DmCreatorBroadcast>>("dmBroadcastList", {
    artistId,
    limit: opts.limit ?? 20,
    ...opt("status", opts.status),
    ...opt("cursor", opts.cursor),
  });
/** v2.4: `broadcastId` and `filter` combine (filter within one broadcast's replies); `filter:"blocked"` lists blocked fans' replies. */
export const dmInbox = (
  artistId: string,
  opts: { broadcastId?: string | null; filter?: DmInboxFilter | null; cursor?: string | null; limit?: number; markSeen?: boolean } = {},
) =>
  dmRequest<DmPage<DmInboxItem>>("dmInbox", {
    artistId,
    limit: opts.limit ?? 20,
    ...inboxQuery(opts),
    ...opt("cursor", opts.cursor),
    ...(opts.markSeen === undefined ? {} : { markSeen: opts.markSeen }),
  });
export const dmReplyHeart = (artistId: string, replyId: string, hearted = true) =>
  dmRequest<{ replyId: string; hearted: boolean }>("dmReplyHeart", { artistId, replyId, hearted });
export const dmReplyPrivate = (artistId: string, requestId: string, text: string, to: { replyId: string } | { uid: string }) =>
  dmRequest<{ messageId: string; replay: boolean; threadKey: string; message: DmMessage }>("dmReplyPrivate", { ...to, artistId, requestId, text });
export const dmReplyHide = (artistId: string, replyId: string, hidden: boolean, opts: { report?: boolean; reason?: string } = {}) =>
  dmRequest<{ replyId: string; hidden: boolean; hiddenBy: DmInboxItem["hiddenBy"]; reported: boolean }>("dmReplyHide", {
    artistId,
    replyId,
    hidden,
    ...(opts.report ? { report: true } : {}),
    ...opt("reason", opts.reason),
  });
export const dmBlockFan = (artistId: string, uid: string, blocked: boolean, reason?: string) =>
  dmRequest<{ threadKey: string; uid: string; blocked: boolean }>("dmBlockFan", { artistId, uid, blocked, ...opt("reason", reason) });
export const dmInsights = (artistId: string, month?: string | null) => dmRequest<DmInsights>("dmInsights", { artistId, ...opt("month", month) });
/** One DM image: mediaUploadImage {contentId:"dm", purpose:"dm-image"} (⚠️ still behind artist_hub_enabled). */
export const dmImageUpload = (artistId: string, assetId: string, base64: string) =>
  dmRequest<{ assetId: string; width: number; height: number }>("mediaUploadImage", { artistId, contentId: "dm", assetId, purpose: "dm-image", base64 });
/** B-32 voice clip (broadcast or welcome): m4a / ADTS AAC, ≤2 MB, ≤60 s, no transcode. */
export const dmVoiceUpload = (artistId: string, assetId: string, base64: string) =>
  dmRequest<{ assetId: string; kind: "audio"; durationMs: number; mime: string }>("mediaUploadAudio", {
    artistId,
    contentId: "dm",
    assetId,
    purpose: "dm-voice",
    base64,
  });

/** B-32 DM video: the vlog upload actions with contentId "dm" (no draft; transcoded by artistVodJobs). */
export interface DmVideoState {
  assetId: string;
  status: "allocating" | "uploading" | "uploaded" | "processing" | "processed" | "ready" | "failed" | string;
  bytes: number;
  mime: string;
  retryable: boolean;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  uploadUrl?: string;
}
export const dmVideoBegin = (artistId: string, assetId: string, file: { bytes: number; md5: string; mime: string }) =>
  dmRequest<DmVideoState>("videoBegin", { artistId, contentId: "dm", assetId, ...file });
export const dmVideoComplete = (artistId: string, assetId: string) =>
  dmRequest<DmVideoState>("videoComplete", { artistId, contentId: "dm", assetId });
export const dmVideoStatus = (artistId: string, assetId: string) => dmRequest<DmVideoState>("videoStatus", { artistId, contentId: "dm", assetId });
/**
 * PUTs the whole file to the GCS resumable session from videoBegin (only https://storage.googleapis.com/upload/storage/v1/b/…).
 * ⚠️ A browser upload needs the session to be created with the CMS origin (createResumableUpload {origin}); see C-03 notes.
 */
export async function dmVideoPut(uploadUrl: string, file: Blob): Promise<void> {
  let url: URL;
  try {
    url = new URL(uploadUrl);
  } catch {
    throw new DmError("上傳連結無效", "INVALID_UPLOAD", undefined, 0);
  }
  if (url.protocol !== "https:" || url.host !== "storage.googleapis.com" || !url.pathname.startsWith("/upload/storage/v1/b/")) {
    throw new DmError("上傳連結無效", "INVALID_UPLOAD", undefined, 0);
  }
  let response: Response;
  try {
    response = await fetch(url, {
      method: "PUT",
      headers: { "Content-Range": `bytes 0-${file.size - 1}/${file.size}` },
      body: file,
      signal: AbortSignal.timeout(30 * 60000),
    });
  } catch {
    throw new DmError(dmErrorMessage("UPLOAD_INTERRUPTED"), "UPLOAD_INTERRUPTED", undefined, 0);
  }
  if (response.status === 404 || response.status === 410) throw new DmError(dmErrorMessage("UPLOAD_EXPIRED"), "UPLOAD_EXPIRED", undefined, response.status);
  if (response.status !== 200 && response.status !== 201) {
    throw new DmError(dmErrorMessage("UPLOAD_INTERRUPTED"), "UPLOAD_INTERRUPTED", undefined, response.status);
  }
}

/* ops (staff only: users/{uid}.roles has admin|staff; not behind the RC gate) */
export const dmModerationList = (opts: { status?: "open" | "resolved"; artistId?: string | null; cursor?: string | null; limit?: number } = {}) =>
  dmRequest<DmPage<DmReport>>("dmModerationList", {
    status: opts.status ?? "open",
    limit: opts.limit ?? 20,
    ...opt("artistId", opts.artistId),
    ...opt("cursor", opts.cursor),
  });
export const dmModerate = (reportId: string, decision: DmModerationDecision, note?: string) =>
  dmRequest<DmReport>("dmModerate", { reportId, decision, ...opt("note", note) });
export interface DmRefundClaim {
  id: string;
  uid: string;
  artistId: string;
  platform: "ios" | "android" | "coin";
  status: DmRefundClaimStatus;
  periodStart: number | null;
  createdAt: number | null;
  resolvedAt: number | null;
  lastError: string | null;
}
/**
 * ⚠️ Not in contract v2.3: dmRefundClaims is server-only, so the list needs a staff action `dmRefundClaimList`
 * (requested from backend in C-03). Shape agreed: {claims:[…], nextCursor}.
 */
export const dmRefundClaimList = (
  opts: { status?: DmRefundClaimStatus | null; artistId?: string | null; platform?: DmRefundClaim["platform"] | null; cursor?: string | null; limit?: number } = {},
) =>
  dmRequest<{ claims: DmRefundClaim[]; nextCursor: string | null }>("dmRefundClaimList", {
    limit: opts.limit ?? 20,
    ...opt("status", opts.status),
    ...opt("artistId", opts.artistId),
    ...opt("platform", opts.platform),
    ...opt("cursor", opts.cursor),
  });
export const dmChannelSuspend = (artistId: string, mode: DmSuspendMode, reason?: string) =>
  dmRequest<{ artistId: string; status: "suspended" | "closed" | "active"; statusReason: string | null; saleStopped: false }>("dmChannelSuspend", {
    artistId,
    mode,
    ...opt("reason", reason),
  });

/* ---------- C-04: 創作者申請審核（B-40, contract §1e v2.6; staff only） ---------- */

export interface CreatorApplication {
  uid: string;
  displayName: string;
  kind: CreatorKind;
  links: string[];
  bio: string;
  status: CreatorApplicationStatus;
  createdAt: number | null;
  updatedAt: number | null;
  reviewedBy?: string | null;
  reviewedAt?: number | null;
  reason?: string | null;
  artistId?: string | null;
  email?: string | null;
  photoURL?: string | null;
}
/** Same artistHub endpoint as the DM actions; errors carry `{error, reason}` as DmError (code `CREATOR_APPLICATION_STATE` = 409). */
async function creatorRequest<T>(action: string, body: Record<string, unknown>): Promise<T> {
  if (!artistHubEnabled) throw new DmError(creatorErrorMessage("FEATURE_NOT_READY"), "FEATURE_NOT_READY", undefined, 0);
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new DmError(creatorErrorMessage("UNAUTHENTICATED"), "UNAUTHENTICATED", undefined, 401);
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action }),
    signal: AbortSignal.timeout(30000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; reason?: string };
  if (!response.ok) throw new DmError(creatorErrorMessage(data.error, data.reason, response.status), data.error ?? "", data.reason, response.status);
  return data as T;
}
export const creatorApplicationList = async (opts: { status?: CreatorApplicationStatus | null; cursor?: string | null; limit?: number } = {}) => {
  // 後端（B-40）實際回 `{items, nextCursor}`，每筆的帳號頭像／名稱在 `account`；這裡正規化成頁面用的形狀，兩種都接受。
  type Raw = CreatorApplication & { account?: { displayName?: string | null; photoURL?: string | null } | null };
  const data = await creatorRequest<{ applications?: Raw[]; items?: Raw[]; nextCursor?: string | null }>("creatorApplicationList", {
    limit: opts.limit ?? 20,
    ...opt("status", opts.status),
    ...opt("cursor", opts.cursor),
  });
  const rows = data.applications ?? data.items ?? [];
  const applications: CreatorApplication[] = rows.map((r) => ({
    ...r,
    links: Array.isArray(r.links) ? r.links : [],
    photoURL: r.photoURL ?? r.account?.photoURL ?? null,
    email: r.email ?? null,
  }));
  return { applications, nextCursor: data.nextCursor ?? null };
};
/**
 * Bind an existing artist (`{uid, artistId, force?}`) or create one (`{uid, newArtist}`); build the body with `buildApproveBody`.
 * B-45: 409 `ARTIST_ALREADY_OWNED` (+ `owners`) when the artist has another owner and `force` is not set; 409 `CREATOR_SELF_APPROVE`.
 */
export const creatorApplicationApprove = (body: CreatorApproveBody) =>
  creatorRequest<{ artistId: string; status: "approved" }>("creatorApplicationApprove", { ...body });
/** B-45 staff: active "artist" members of an artist (shown before binding an application to it). */
export const artistOwnersGet = (artistId: string) =>
  creatorRequest<{ artistId: string; owners: ArtistOwner[] }>("artistOwnersGet", { artistId });
export const creatorApplicationReject = (uid: string, reason: string) =>
  creatorRequest<{ status: "rejected" }>("creatorApplicationReject", { uid, reason });

/* ---------- C-05: 鐵粉月卡自助申請審核＋@handle（B-42/B-43, contract v2.7 — not in repo yet; shapes agreed with backend） ---------- */

export interface FanPassConfigRequest {
  requestId: string;
  artistId: string;
  artistName: string;
  uid: string;
  /** Requested values; a missing field is not part of the request. */
  priceTier?: number | null;
  benefits?: string[] | null;
  saleEnabled?: boolean | null;
  welcomeText?: string | null;
  status: FanPassRequestStatus;
  createdAt: number | null;
  reviewedBy?: string | null;
  reviewedAt?: number | null;
  reason?: string | null;
  /** status `approved_manual`: what staff still have to do by hand (store console etc.). */
  manualSteps?: string[];
}
export const fanPassConfigRequestList = (opts: { status?: FanPassRequestStatus | null; cursor?: string | null; limit?: number } = {}) =>
  creatorRequest<{ items: FanPassConfigRequest[]; nextCursor: string | null }>("fanPassConfigRequestList", {
    limit: opts.limit ?? 20,
    ...opt("status", opts.status),
    ...opt("cursor", opts.cursor),
  });
/** 409 `FAN_PASS_REQUEST_STATE` when the request was already handled. */
export const fanPassConfigRequestApprove = (requestId: string) =>
  creatorRequest<{ requestId: string; status: "approved" | "approved_manual"; applied: boolean; manualSteps?: string[] }>(
    "fanPassConfigRequestApprove",
    { requestId },
  );
export const fanPassConfigRequestReject = (requestId: string, reason: string) =>
  creatorRequest<{ requestId: string; status: "rejected" }>("fanPassConfigRequestReject", { requestId, reason });
/** Staff may change a handle any time (the once-only limit applies to artists). 400 INVALID_ARGUMENT reason "handle" / 409 HANDLE_TAKEN. */
export const creatorSetHandle = (artistId: string, handle: string) =>
  creatorRequest<{ artistId: string; handle: string }>("creatorSetHandle", { artistId, handle });

/* ---------- Q-02 動態檢舉佇列（contract §4「v3.4 補充」; staff only） ---------- */

export interface ArtistModerationSnapshot {
  text: string;
  title: string | null;
  contentType: string | null;
  authorUid: string | null;
  authorKind: "artist" | "fan";
  authorName: string | null;
  artistName: string | null;
  previewUrl: string | null;
  parentId: string | null;
}
export interface ArtistModerationCase {
  id: string;
  type: ArtistModerationType;
  status: ArtistModerationStatus;
  targetId: string | null;
  contentId: string | null;
  artistId: string | null;
  commentId: string | null;
  reportCount: number;
  reasons: Record<string, number>;
  lastReason: ArtistReportReason;
  snapshot: ArtistModerationSnapshot;
  recentReports: { reason: string; note: string; createdAt: number | null }[];
  createdAt: number | null;
  openedAt: number | null;
  lastReportedAt: number | null;
  overdue: boolean;
  resolution: { decision: ArtistModerationDecision | null; note: string; by: string | null; at: number | null } | null;
  resolvedAt: number | null;
  allowedDecisions: ArtistModerationDecision[];
}
export interface ArtistModerationPage {
  items: ArtistModerationCase[];
  nextCursor: string | null;
  /** Only on status "open": the whole queue, and how many wait past 24 hours. */
  openCount?: number;
  overdueCount?: number;
}
export class ModerationError extends Error {
  constructor(message: string, readonly code: string, readonly reason: string | undefined, readonly status: number) {
    super(message);
  }
}
// Not behind artistHubEnabled: the report queue must work in PROD as soon as artistHub is deployed (24 h SLA).
async function moderationRequest<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new ModerationError("請重新登入後操作", "UNAUTHENTICATED", undefined, 401);
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ ...body, action }),
    signal: AbortSignal.timeout(30000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; reason?: string };
  if (!response.ok) {
    throw new ModerationError(moderationErrorMessage(data.error, data.reason, response.status), data.error ?? "", data.reason, response.status);
  }
  return data as T;
}
export const artistModerationList = (
  opts: { status?: ArtistModerationStatus; type?: ArtistModerationType | null; cursor?: string | null; limit?: number } = {},
) =>
  moderationRequest<ArtistModerationPage>("moderationList", {
    status: opts.status ?? "open",
    limit: opts.limit ?? 20,
    ...opt("type", opts.type),
    ...opt("cursor", opts.cursor),
  });
export const artistModerationResolve = (id: string, decision: ArtistModerationDecision, note: string) =>
  moderationRequest<ArtistModerationCase>("moderationResolve", { id, decision, note: note.trim() });

/* ---------- 精選藝人排序（featuredList / featuredSetOrder，staff） ---------- */

async function featuredRequest(body: Record<string, unknown>): Promise<FeaturedPage> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error(featuredErrorMessage("UNAUTHENTICATED", 401));
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(30000),
  });
  const data = (await response.json().catch(() => ({}))) as Partial<FeaturedPage> & { error?: string };
  if (!response.ok) throw new Error(featuredErrorMessage(data.error, response.status));
  return { items: data.items ?? [], visibleCount: data.visibleCount ?? 10, maxPinned: data.maxPinned ?? 30 };
}
export const featuredList = () => featuredRequest({ action: "featuredList" });
export const featuredSetOrder = (artistIds: string[]) => featuredRequest({ action: "featuredSetOrder", artistIds });

/* ---------- 藝人總覽 / 待審內容（artistAdminList / contentReviewQueue，staff） ---------- */

async function artistAdminRequest(body: Record<string, unknown>): Promise<unknown> {
  if (!artistHubEnabled) throw new Error("藝人專區尚未開放此環境");
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error(artistAdminErrorMessage("UNAUTHENTICATED", 401));
  const response = await fetch(`https://asia-east1-${app.options.projectId}.cloudfunctions.net/artistHub`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(60000),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new Error(artistAdminErrorMessage(data.error, response.status));
  return data;
}
/** Every actor (≤500) or the given artistIds (≤200) with profile / verification / owners / counts. */
export const artistAdminList = async (artistIds?: string[]): Promise<ArtistAdminListResult> =>
  toAdminList(await artistAdminRequest({ action: "artistAdminList", ...(artistIds ? { artistIds } : {}) }));
/** pending_review drafts across all artists, oldest submission first. */
export const contentReviewQueue = async (opts: { cursor?: string | null; limit?: number } = {}): Promise<ReviewQueuePage> =>
  toReviewPage(
    await artistAdminRequest({ action: "contentReviewQueue", limit: reviewQueueLimit(opts.limit), ...opt("cursor", opts.cursor) }),
  );
